import { v4 } from 'uuid';
import { PaginationMetadata, ParticipantFilter, UpcomingEvent } from '../../../API/types';
import { create } from 'zustand';
import { setAccumulatedItems } from '../utils';
import { ProviderIntegration } from '../../../redux/typings/store';
import { storeFilters } from '../../saved-filters/store';
import { getFilters } from '../../saved-filters/get';
import { setCurrentView } from '../../saved-filters/set-current-view';
import { removeCurrentView } from '../../saved-filters/remove-current-view';
import { SavedFiltersFilterItem } from '../../saved-filters/types';

export const UPCOMING_MEETINGS_PAGE_KEY = 'upcoming-meetings';

const defaultDateRange: [null, null] = [null, null];
const defaultMetadata = {
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
  totalPages: 0,
};

export type PastMeetingStore = ReturnType<typeof createUpcomingMeetingsStore>;

interface UpcomingMeetingsState {
  events: UpcomingEvent[];
  isFetching: boolean;
  updateAccounts: ProviderIntegration[];
  paginationMetadata: PaginationMetadata;
  page: number;
  pageSize: number;
  searchTerm: string;
  meetingType: string;
  mType: string | null;
  canLoadData: boolean;
  currentFilterView: string;
  participants: ParticipantFilter[]
  eventsFetched: (events: UpcomingEvent[]) => unknown;
  fetchStatusChanged: (isFetching: boolean) => unknown;
  updateAccountsFiltered: (accounts: ProviderIntegration[]) => unknown;
  pageChanged: (value: number) => unknown;
  paginationMetadataSet: (metadata: PaginationMetadata) => unknown;
  searchTermChanged: (value: string) => unknown;
  meetingTypeChanged: (value: string) => unknown;
  resetFilters: () => unknown;
  keepFetching: () => unknown;
  refetch: () => unknown;
  setPurposeStatement: (id: string, statement: string) => unknown;
  updateMeetingAccount: (
    id: string,
    account: { id: string; name: string; provider: string; mappingId: string } | null,
  ) => unknown;
  updateMType: (mType: string | null) => unknown;
  updateFiltersState: (filters: PersistFiltersType) => void;
  saveFiltersView: (viewName: string) => void;
  setCurrentFilterView: (viewId: string | null) => void;
  setParticipants: (participants: ParticipantFilter[]) => void;

  //Internal
  fetchId: string;
}

type PersistFiltersType = Record<string, [Date, Date] | [null, null] | string | boolean | number | null | ProviderIntegration[]>

const persistFilters = (newFilters: PersistFiltersType) => {
  const currentFiltersObject = JSON.parse(localStorage.getItem('pageFilters') || '{}');
  const currentPageFilters = currentFiltersObject[UPCOMING_MEETINGS_PAGE_KEY] || {};
  const newFiltersObject = {
    ...currentPageFilters,
    ...newFilters,
  };
  currentFiltersObject[UPCOMING_MEETINGS_PAGE_KEY] = newFiltersObject;
  localStorage.setItem('pageFilters', JSON.stringify(currentFiltersObject));
}

export const createUpcomingMeetingsStore = () => {
  return create<UpcomingMeetingsState>((set, get) => {
    return {
      events: [],
      isFetching: false,
      updateAccounts: [],
      contacts: [],
      dateRange: defaultDateRange,
      paginationMetadata: defaultMetadata,
      page: 1,
      pageSize: 20,
      searchTerm: '',
      meetingType: 'all',
      mType: null,
      canLoadData: false,
      currentFilterView: '',
      participants: [],
      // Filters
      updateFiltersState: filters => set(filters),
      updateAccountsFiltered: accounts =>
        set(store => {
          if (store.updateAccounts.length !== accounts.length) {
            persistFilters({ updateAccounts: accounts });
            return { updateAccounts: accounts, page: 1, events: [] };
          } else {
            const set = new Set(accounts.map(account => account.id));
            for (const acc of store.updateAccounts) {
              if (set.has(acc.id)) {
                set.delete(acc.id);
              } else {
                set.add(acc.id);
              }
            }
            if (set.size !== 0) {
              persistFilters({ updateAccounts: accounts });
              return { updateAccounts: accounts, page: 1, events: [] }
            }
            return store;
          }
        }),
      searchTermChanged: (value: string) => {
        set({ searchTerm: value, page: 1, events: [] })
      },
      meetingTypeChanged: (value: string) => {
        set({ meetingType: value, page: 1, events: [] })
        persistFilters({ meetingType: value });
      },
      resetFilters: () => {
        set({
          events: [],
          updateAccounts: [],
          meetingType: 'all',
          mType: null,
          searchTerm: '',
          paginationMetadata: defaultMetadata,
          page: 1,
        })
        const currentFiltersObject = JSON.parse(localStorage.getItem('pageFilters') || '{}');
        delete currentFiltersObject[UPCOMING_MEETINGS_PAGE_KEY];
        localStorage.setItem('pageFilters', JSON.stringify(currentFiltersObject));
      },

      pageChanged: (value: number) => set({ page: value }),
      paginationMetadataSet: metadata => set({ paginationMetadata: metadata }),

      keepFetching: () => {
        set(state => {
          if (state.paginationMetadata.page < state.paginationMetadata.totalPages) {
            return { page: state.page + 1 };
          }
          return state;
        });
      },
      eventsFetched: events => {
        set(state => {
          return { events: setAccumulatedItems(state.events, events, true) };
        });
      },
      refetch: () => {
        set({
          paginationMetadata: defaultMetadata,
          page: 1,
          fetchId: v4(),
          events: [],
        });
      },
      fetchStatusChanged: isFetching => set({ isFetching }),
      setPurposeStatement: (id, statement) =>
        set(state => {
          const event = state.events.find(event => event.id === id);
          if (event) event.purposeStatement = statement;
          return { events: state.events };
        }),
      updateMeetingAccount: (id, account) =>
        set(state => {
          const event = state.events.find(event => event.id === id);
          if (event) event.account = account;
          return { events: state.events };
        }),
      updateMType: mType => {
        set({
          mType,
          page: 1,
          events: [],
        })
        persistFilters({ mType });
      },
      setParticipants: participants => set({ participants, page: 1, events: [] }),

      saveFiltersView: (viewName: string) => {
        const state = get();
        const filters: Record<string, SavedFiltersFilterItem> = {
          searchTerm: {
            value: state.searchTerm,
            label: 'Search Term',
            type: 'text'
          },
          meetingType: {
            value: state.meetingType,
            label: 'Meeting Type',
            type: 'text'
          },
          mType: {
            value: state.mType,
            label: 'Invitee Scope',
            type: 'text'
          },
        }

        storeFilters({ pageKey: UPCOMING_MEETINGS_PAGE_KEY, pageTitle: 'Upcoming Meetings Dashboard', viewName, filters });
      },
      setCurrentFilterView: (viewId: string | null) => {
        if (!viewId) {
          removeCurrentView(UPCOMING_MEETINGS_PAGE_KEY);
          const { refetch, resetFilters } = get()
          if (refetch) refetch();
          if (resetFilters) resetFilters();
          set({
            currentFilterView: '',
          });
          return;
        }

        const filters = getFilters({ pageKey: UPCOMING_MEETINGS_PAGE_KEY, viewId });
        setCurrentView(UPCOMING_MEETINGS_PAGE_KEY, viewId);

        const { refetch } = get()
        if (refetch) refetch();

        set({
          ...filters,
          currentFilterView: viewId,
        })
      },

      // Internal
      fetchId: v4(),
    };
  });
};
